import React from 'react';
import qs from 'qs';
import {isEntitled} from '../../../shared/store';
import {DescriptiveText, GridTile, RegText} from '../../../shared';
import layoutMap from './layoutMap.json';
import lazy from 'lazy.js';
import tileImageMap from './tileImageMap.json';
import {Insights} from '../../widgets/containers';
import {
  ExposureGraph,
  HoldingsTable,
  TransactionsTable,
  PortfolioInfo,
  AccountInfo,
  BalanceGraph,
  AccountsList,
  GroupInfo,
  WealthAlerts,
  PerformanceGraph,
} from '../../widgets/containers';
import history from '../../../history';
import {buildQueryContext} from '../../../shared/utils/utils';
import {ProbabilityOfSuccess, RiskManagement} from '../../widgets';

export const tileCapMap = {
  PORTFOLIO: {
    5: 'B',
    6: 'B',
    7: 'B',
  },

  ACCOUNT: {
    5: 'A',
    6: 'C',
  },

  GROUP: {
    5: 'B',
    6: 'B',
  },
};

const defaultTileOrders = {
  PORTFOLIO: [
    'balanceHistory',
    'performance',
    'accountsList',
    'exposure',
    'insights',
    'holdings',
    'transactions',
    'portfolioInfo',
    'WealthAlerts',
    'risk',
    'probabilityOfSuccess',
  ],

  ACCOUNT: [
    'balanceHistory',
    'performance',
    'holdings',
    'exposure',
    'transactions',
    'accountInfo',
    'insights',
  ],

  GROUP: [
    'balanceHistory',
    'performance',
    'groupInfo',
    'holdings',
    'exposure',
    'transactions',
    'insights',
  ],
};

export const getDefaultTileOrder = (authTiles, numTiles, context, preferences) => {
  const authTileKeys = lazy(authTiles).pluck('key').toArray();
  const baseOrder = [...defaultTileOrders[context.type]].filter(tile => authTileKeys.includes(tile)).slice(0, numTiles);
  const tileOrder = {};

  for (const tile of authTiles) {
    const orderIdx = baseOrder.indexOf(tile.key);

    if (orderIdx < 0) {continue;}
    if ((Object.keys(tileOrder).length >= numTiles)) {break;}

    let tileImageUrl;

    if (tile.key === 'risk') {
      tileImageUrl = tileImageMap[tile.key].url[preferences.riskPartner];
    } else if (tile.key === 'probabilityOfSuccess') {
      tileImageUrl = tileImageMap[tile.key].url[preferences.financialPlanningPartner];
    } else {
      tileImageUrl = tileImageMap[tile.key].url;
    }

    tileOrder[tile.key] = {
      title: tile.title,
      url: tileImageUrl,
      order: orderIdx + 1,
    };
  }

  return tileOrder;
};

export const getAuthTileData = (authTiles, currentTileData, context, preferences) => {
  const tileData = {};

  if (!authTiles || !currentTileData || !context) {return tileData;}

  for (const tile of authTiles) {
    let tileImageUrl;

    if (tile.key === 'risk') {
      tileImageUrl = tileImageMap[tile.key].url[preferences.riskPartner];
    } else if (tile.key === 'probabilityOfSuccess') {
      tileImageUrl = tileImageMap[tile.key].url[preferences.financialPlanningPartner];
    } else {
      tileImageUrl = tileImageMap[tile.key].url;
    }

    tileData[tile.key] = {
      title: tile.title,
      url: tileImageUrl,
      selected: currentTileData.hasOwnProperty(tile.key),
      order: currentTileData.order,
      hide: tile.hide,
    };
  }

  return tileData;
};

export function getLayoutsFromKeys(layoutSelectionKey, tileLayoutData) {
  const layouts = {
    xl: [],
    lg: [],
    md: [],
    sm: [],
    xs: [],
  };

  if (!layoutSelectionKey || !tileLayoutData) {return layouts;}

  Object.keys(tileLayoutData).forEach(key => {
    ['xl', 'lg', 'md', 'sm', 'xs'].forEach(size => {
      layouts[size].push({i: key, ...layoutMap[layoutSelectionKey][size][String(tileLayoutData[key].order)]});
    });
  });

  return layouts;
}

export function getTiles(config) {
  const {
    context,
    container,
    openModal,
    showPoS,
    showRisk,
  } = config;

  if (!context || !container) {
    return [];
  }

  const basePath = window.location.pathname;

  return [
    {
      key: 'balanceHistory',
      title: 'Balance',
      jsx: <GridTile key="balanceHistory"
                     style={{padding: '10px'}}
                     title="Balance"
                     maximize={() => history.push(`${basePath}/balance?${buildQueryContext(context)}`)}>
        <BalanceGraph containerID={context.containerID}/>
      </GridTile>,
    },
    (context.type === 'PORTFOLIO')
    && {
      key: 'accountsList',
      title: 'Accounts',
      jsx: <GridTile key="accountsList"
                     style={{padding: '10px'}}
                     title="Accounts"
                     maximize={() => history.push(`${basePath}/accounts?${buildQueryContext(context)}`)}>
        <AccountsList containerID={context.containerID}/>
      </GridTile>,
    },
    {
      key: 'exposure',
      title: 'Exposure',
      jsx: <GridTile key="exposure" title="Exposure"
                     style={{padding: '10px'}}
                     maximize={() => history.push(`${basePath}/exposure?${buildQueryContext(context)}&exposureCategory=${qs.parse(
                       window.location.search
                     ).exposureCategory}`)}>
        <ExposureGraph cat="containers"
                       catID={context.containerID}
                       useInvestorPreference={true}
                       sliceAction={(slice, category, types) => context && openModal({
                         type: 'ExposureSecuritiesModal',
                         containerID: context.containerID,
                         category: category.value,
                         types: types.map(t => ({label: t.name, value: t.id})),
                         initialType: {
                           label: category.value === 'REGION' ? (slice.regionName || slice.name) : slice.name,
                           value: slice.ident,
                         },
                       })}
        />
      </GridTile>,
    },
    {
      key: 'holdings',
      title: 'Holdings',
      jsx: <GridTile key="holdings" title="Holdings"
                     style={{padding: '10px'}}
                     maximize={() => history.push(`${basePath}/holdings?${buildQueryContext(context)}`)}>
        {container && <HoldingsTable container={container} rowsPerPage={10}/>}
      </GridTile>,
    },
    {
      key: 'transactions',
      title: 'Transactions',
      jsx: <GridTile key="transactions" title="Transactions"
                     style={{padding: '10px'}}
                     maximize={() => history.push(`${basePath}/transactions?${buildQueryContext(context)}&transactionPeriod=${qs.parse(window.location.search).transactionPeriod}`)}>
        <TransactionsTable container={container} rowsPerPage={10}/>
      </GridTile>,
    },
    isEntitled([{group: 'performance', feature: 'core', access: 'r'}])
    && {
      key: 'performance',
      title: 'Performance',
      jsx: <GridTile key="performance"
                     title="Performance"
                     style={{padding: '10px'}}
                     maximize={() => history.push(`${basePath}/performance?${buildQueryContext(context)}`)}>
        <PerformanceGraph containerID={container && context.containerID} context={context}/>
      </GridTile>,
    },
    (context.type === 'ACCOUNT')
    && {
      key: 'accountInfo',
      title: 'Account Info',
      jsx: <GridTile key="accountInfo"
                     title="Account Info"
                     style={{padding: '10px'}}
                     maximize={() => history.push(`${basePath}/info?${buildQueryContext(context)}`)}>
        <AccountInfo containerID={context.containerID}/>
      </GridTile>,
    },
    (context.type === 'PORTFOLIO')
    && {
      key: 'risk',
      title: 'Risk Management',
      jsx: <GridTile key='risk'
                     title='Risk Management'
                     style={{padding: '10px'}}
                     maximize={()=>history.push(`${basePath}/risk?${buildQueryContext(context)}`)}>
        {showRisk ? <RiskManagement context={context}/> : <DescriptiveText>Risk Data is no longer available.</DescriptiveText>}
      </GridTile>,
      hide: !showRisk,
    },
    (context.type === 'PORTFOLIO')
    && {
      key: 'probabilityOfSuccess',
      title: 'Probability of Success',
      jsx: <GridTile key="probabilityOfSuccess"
                     title="Probability of Success"
                     style={{padding: '10px'}}
                     maximize={() => history.push(`${basePath}/pos?${buildQueryContext(context)}`)}>
        {showPoS
          ? <ProbabilityOfSuccess context={context}/>
          : <DescriptiveText>Probability of Success Data is no longer
              available.</DescriptiveText>} {/* ToDo: Update Descriptive Text pending reqs*/}
      </GridTile>,
      hide: !showPoS,
    },
    (context.type === 'GROUP')
    && {
      key: 'groupInfo',
      title: 'Group Info',
      jsx: <GridTile key="groupInfo"
                     title="Group Info"
                     style={{padding: '10px'}}
                     maximize={() => history.push(`${basePath}/groupInfo?${buildQueryContext(context)}`)}>
        <GroupInfo containerID={context.containerID}/>
      </GridTile>,
    },
    (context.type === 'PORTFOLIO')
    && {
      key: 'WealthAlerts',
      title: 'WealthAlerts',
      jsx: <GridTile key="WealthAlerts"
                     title={<RegText>WealthAlerts</RegText>}
                     style={{padding: '10px'}}
                     maximize={() => history.push(`${basePath}/wealthalerts?${buildQueryContext(context)}`)}>
        <WealthAlerts/>
      </GridTile>,
    },
    (context.type === 'PORTFOLIO')
    && {
      key: 'portfolioInfo',
      title: 'Portfolio Info',
      jsx: <GridTile key="portfolioInfo"
                     title="Portfolio Info"
                     style={{padding: '10px'}}
                     maximize={() => history.push(`${basePath}/info?${buildQueryContext(context)}`)}>
        <PortfolioInfo containerID={context.containerID}/>
      </GridTile>,
    },
    {
      key: 'insights',
      title: 'Insights',
      jsx: <GridTile key="insights" title="Insights" style={{padding: '10px'}} maximize={() => history.push(`${basePath}/insights`)}>
        <Insights />
      </GridTile>,
    },
  ].filter(i => !!i);
}

export function deleteHidden(obj) {
  const keys = Object.keys(obj);
  const newObj = {...obj};
  keys.forEach(key => {
    if (newObj[key].hide === true) {
      delete newObj[key];
    }
  });
  return newObj;
}

export const getBackPath = () => {
  const pathName = window.location.pathname;
  return pathName.slice(0, pathName.lastIndexOf('/'));
};
